import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import Footer from "../../components/Home/Footer";
import { motion } from "framer-motion";
import { useCart } from "../../context/CartContext";
import StickWomanSpinner from "../../components/Spinners/StickWomanSpinner";

type Product = {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  gallery_images?: string[] | null;
  brandId: number;
  categoryId: number;
  quantity?: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  is_active?: boolean;
};

type ProductVariant = {
  id: string;
  product_id: string;
  size_label: string;
  price: number;
  image_url: string | null;
  weight: number;
  length: number;
  width: number;
  height: number;
  is_active?: boolean;
  sort_order: number;
};

type RecRow = {
  recommended_product_id: string;
  rank: number | null;
};

type CartProduct = Product & {
  variant_id?: string;
  size_label?: string;
};

const shuffle = <T,>(arr: T[]) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const money = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(n || 0));

const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<string>("");
  const [selectedGalleryImage, setSelectedGalleryImage] = useState<string>("");

  const [brandName, setBrandName] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>("");
  const [categoryNames, setCategoryNames] = useState<string[]>([]);

  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  // Recommended + Explore
  const [recommended, setRecommended] = useState<Product[]>([]);
  const [explorePool, setExplorePool] = useState<Product[]>([]);
  const [explorePage, setExplorePage] = useState(0);

  const EXPLORE_PAGE_SIZE = 6;

  const selectedVariant = useMemo(() => {
    return variants.find((v) => v.id === selectedVariantId) || null;
  }, [variants, selectedVariantId]);

  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;
  const primaryImage = selectedVariant?.image_url || product?.image_url || "";
  const galleryImages = useMemo(() => {
    const imageUrls = [
      primaryImage,
      ...((product?.gallery_images || []) as string[]),
    ].filter(Boolean);

    return Array.from(new Set(imageUrls));
  }, [primaryImage, product?.gallery_images]);
  const displayImage = selectedGalleryImage || primaryImage;

  const exploreItems = useMemo(() => {
    const start = explorePage * EXPLORE_PAGE_SIZE;
    return explorePool.slice(start, start + EXPLORE_PAGE_SIZE);
  }, [explorePool, explorePage]);

  const exploreHasPrev = explorePage > 0;
  const exploreHasNext =
    (explorePage + 1) * EXPLORE_PAGE_SIZE < explorePool.length;

  useEffect(() => {
    setSelectedGalleryImage("");
  }, [id, selectedVariantId]);

  useEffect(() => {
    if (!id) return;

    const run = async () => {
      setLoading(true);

      // 1) product
      const { data: p, error: pErr } = await supabase
        .from("Product")
        .select("*")
        .eq("id", id)
        .single();

      if (pErr || !p) {
        console.error("❌ Product fetch error:", pErr);
        setProduct(null);
        setLoading(false);
        return;
      }

      setProduct(p as Product);
      setQuantity(1);

      const { data: variantData, error: variantErr } = await supabase
        .from("ProductVariant")
        .select("*")
        .eq("product_id", p.id)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (variantErr) {
        console.error("❌ Variant fetch error:", variantErr);
        setVariants([]);
        setSelectedVariantId("");
      } else {
        const activeVariants = (variantData || []) as ProductVariant[];
        setVariants(activeVariants);
        if (activeVariants.length > 0) {
          setSelectedVariantId(activeVariants[0].id);
        } else {
          setSelectedVariantId("");
        }
      }

      const [bRes, cRes] = await Promise.all([
        supabase.from("Brand").select("name").eq("id", p.brandId).single(),
        supabase.from("Category").select("name").eq("id", p.categoryId).single(),
      ]);

      setBrandName(bRes.data?.name || "");
      setCategoryName(cRes.data?.name || "");

      const { data: productCategoryRows, error: productCategoryError } =
        await supabase
          .from("product_categories")
          .select("category_id")
          .eq("product_id", p.id);

      if (productCategoryError) {
        console.error("❌ Product category fetch error:", productCategoryError);
        setCategoryNames(cRes.data?.name ? [cRes.data.name] : []);
      } else {
        const linkedCategoryIds = Array.from(
          new Set([
            p.categoryId,
            ...((productCategoryRows || []) as any[]).map((row) =>
              Number(row.category_id)
            ),
          ])
        ).filter((id) => Number.isFinite(id));

        if (linkedCategoryIds.length > 0) {
          const { data: linkedCategories, error: linkedCategoriesError } =
            await supabase
              .from("Category")
              .select("id, name")
              .in("id", linkedCategoryIds);

          if (linkedCategoriesError) {
            console.error("❌ Linked category fetch error:", linkedCategoriesError);
            setCategoryNames(cRes.data?.name ? [cRes.data.name] : []);
          } else {
            const categoryNameById = new Map(
              (linkedCategories || []).map((category: any) => [
                category.id,
                category.name,
              ])
            );
            setCategoryNames(
              linkedCategoryIds
                .map((id) => categoryNameById.get(id))
                .filter(Boolean) as string[]
            );
          }
        }
      }

      const { data: recRows, error: recErr } = await supabase
        .from("product_recommendations")
        .select("recommended_product_id, rank")
        .eq("product_id", p.id)
        .eq("type", "recommended")
        .eq("is_active", true)
        .order("rank", { ascending: true });

      if (recErr) console.error("❌ Recommendations fetch error:", recErr);

      const recIds = ((recRows || []) as RecRow[])
        .map((r) => r.recommended_product_id)
        .filter(Boolean);

      if (recIds.length > 0) {
        const { data: recProducts, error: recPerr } = await supabase
          .from("Product")
          .select("*")
          .in("id", recIds);

        if (recPerr) {
          console.error("❌ Recommended products fetch error:", recPerr);
        }

        const map = new Map<string, Product>();
        (recProducts || []).forEach((x: any) => map.set(x.id, x));

        setRecommended(
          recIds.map((rid) => map.get(rid)).filter(Boolean) as Product[]
        );
      } else {
        setRecommended([]);
      }

      const { data: pool, error: poolErr } = await supabase
        .from("Product")
        .select("*")
        .neq("id", p.id)
        .limit(60);

      if (poolErr) console.error("❌ Explore pool fetch error:", poolErr);

      const activeOnly = (pool || []).filter((x: any) => x.is_active !== false);
      setExplorePool(shuffle(activeOnly as Product[]));
      setExplorePage(0);

      setLoading(false);
    };

    run();
  }, [id]);

  const handleAddToCart = (prod: Product, qty: number) => {
    if (!prod || qty < 1) return;

    const cartProduct: CartProduct = {
      ...prod,
      price: selectedVariant?.price ?? prod.price,
      image_url: selectedVariant?.image_url || prod.image_url,
      weight: selectedVariant?.weight ?? prod.weight,
      height: selectedVariant?.height ?? prod.height,
      length: selectedVariant?.length ?? prod.length,
      width: selectedVariant?.width ?? prod.width,
      variant_id: selectedVariant?.id,
      size_label: selectedVariant?.size_label,
    };

    addToCart(cartProduct as any, qty);
    window.dispatchEvent(new Event("show-cart-popup"));
  };

  const handleRecommendedAdd = (prod: Product) => {
    addToCart(prod as any, 1);
    window.dispatchEvent(new Event("show-cart-popup"));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-start min-h-screen pt-32">
        <StickWomanSpinner />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-6 text-center">
          <h2 className="text-xl font-semibold text-[#5c2f2f]">
            Product not found
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            This item may no longer be available.
          </p>
          <button
            type="button"
            onClick={() => nav(-1)}
            className="mt-4 px-4 py-2 rounded border text-sm hover:bg-gray-50"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#fffafa]">
      <div className="flex-grow w-full max-w-[1580px] mx-auto px-4 sm:px-6 lg:px-10 xl:px-12 py-6 sm:py-10">
        {/* Product details */}
        <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-7 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.22fr)_minmax(420px,0.78fr)] gap-7 sm:gap-9 lg:gap-14 xl:gap-20 items-start">
            {/* Image */}
            <div className="w-full">
              <div className="bg-[#fff6f6] rounded-3xl p-3 sm:p-6 lg:p-8 min-h-[360px] sm:min-h-[580px] lg:min-h-[720px] xl:min-h-[820px] flex items-center justify-center">
                <img
                  src={displayImage}
                  alt={product.name}
                  className="w-full h-[330px] sm:h-[540px] lg:h-[690px] xl:h-[780px] object-contain"
                />
              </div>

              {galleryImages.length > 1 && (
                <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                  {galleryImages.map((imageUrl, index) => {
                    const active = displayImage === imageUrl;

                    return (
                      <button
                        key={`${imageUrl}-${index}`}
                        type="button"
                        onClick={() => setSelectedGalleryImage(imageUrl)}
                        className={`h-20 w-20 flex-none rounded-2xl border bg-white p-1 transition sm:h-24 sm:w-24 ${
                          active
                            ? "border-[#5c2f2f] ring-2 ring-[#5c2f2f]/20"
                            : "border-gray-200 hover:border-[#d7c0b7]"
                        }`}
                        aria-label={`View product image ${index + 1}`}
                      >
                        <img
                          src={imageUrl}
                          alt={`${product.name} ${index + 1}`}
                          className="h-full w-full rounded-xl object-contain"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="w-full lg:pt-2">
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-3">
                {brandName ? (
                  <span className="px-2.5 py-1 rounded-full bg-gray-100">
                    {brandName}
                  </span>
                ) : null}
                {(categoryNames.length > 0 ? categoryNames : categoryName ? [categoryName] : [])
                  .map((name) => (
                    <span key={name} className="px-2.5 py-1 rounded-full bg-gray-100">
                      {name}
                    </span>
                  ))}
              </div>

              <h1 className="text-2xl sm:text-4xl font-bold text-[#5c2f2f]">
                {product.name}
              </h1>

              <p className="mt-4 whitespace-pre-wrap text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed">
                {product.description}
              </p>

              <div className="mt-6 flex items-end gap-4">
                <div>
                  <div className="text-xs text-gray-500">Price</div>
                  <div className="text-3xl sm:text-4xl font-semibold text-[#7a4343]">
                    {money(displayPrice)}
                  </div>
                </div>
              </div>

              {/* Size selector */}
              {variants.length > 0 && (
                <div className="mt-6">
                  <label className="text-xs font-medium text-gray-600 mb-2 block">
                    Size
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {variants.map((variant) => {
                      const active = selectedVariantId === variant.id;
                      return (
                        <button
                          key={variant.id}
                          type="button"
                          onClick={() => setSelectedVariantId(variant.id)}
                          className={`px-4 py-2 rounded-xl border text-sm font-medium transition ${
                            active
                              ? "bg-[#5c2f2f] text-white border-[#5c2f2f]"
                              : "bg-white text-[#5c2f2f] border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {variant.size_label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    min={1}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setQuantity(isNaN(val) || val < 1 ? 1 : val);
                    }}
                    className="w-full border border-[rgba(92,47,47,0.25)] rounded-xl p-3 text-center focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
                  />
                </div>

                <motion.button
                  type="button"
                  onClick={() => handleAddToCart(product, quantity)}
                  whileTap={{ scale: 0.98 }}
                  className="h-[48px] mt-[18px] w-full text-base font-semibold bg-[#a25d5d] text-white rounded-xl shadow hover:bg-[#8f4d4d] transition"
                >
                  Add to Cart
                </motion.button>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => nav(-1)}
                  className="text-sm px-4 py-2.5 rounded-xl border hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => nav(`/products?categoryId=${product.categoryId}`)}
                  className="text-sm px-4 py-2.5 rounded-xl border hover:bg-gray-50"
                >
                  Browse more
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Recommended section ONLY if there are items */}
        {recommended.length > 0 && (
          <div className="mt-10">
            <div className="flex items-end justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg sm:text-2xl font-semibold text-[#5c2f2f]">
                  Recommended Pairings
                </h2>
                <p className="text-xs sm:text-sm text-gray-500">
                  Curated to complement your selection.
                </p>
              </div>
            </div>

            <div className="flex gap-5 overflow-x-auto pb-3">
              {recommended.map((p) => (
                <div
                  key={p.id}
                  className="min-w-[290px] max-w-[290px] sm:min-w-[360px] sm:max-w-[360px] bg-white rounded-3xl shadow-md overflow-hidden transition hover:shadow-lg"
                >
                  <button
                    type="button"
                    onClick={() => nav(`/product/${p.id}`)}
                    className="w-full text-left"
                  >
                    <div className="bg-[#fff6f6] p-5 flex items-center justify-center h-[260px] sm:h-[320px]">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="max-h-[230px] sm:max-h-[280px] w-full object-contain"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-sm sm:text-base font-semibold text-[#5c2f2f] line-clamp-2">
                        {p.name}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {money(p.price)}
                      </div>
                    </div>
                  </button>

                  <div className="px-5 pb-5">
                    <motion.button
                      type="button"
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleRecommendedAdd(p)}
                      className="w-full text-sm font-semibold bg-[#5c2f2f] text-white rounded-xl py-2.5 hover:opacity-90"
                    >
                      Add
                    </motion.button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Explore section */}
        <div className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg sm:text-2xl font-semibold text-[#5c2f2f]">
                Explore More Products
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Discover more favorites across our collection.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!exploreHasPrev}
                onClick={() => setExplorePage((p) => Math.max(0, p - 1))}
                className="px-3 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!exploreHasNext}
                onClick={() => setExplorePage((p) => p + 1)}
                className="px-3 py-2 rounded-xl border text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => nav(`/products`)}
                className="px-3 py-2 rounded-xl bg-black text-white text-sm hover:opacity-90"
              >
                View all
              </button>
            </div>
          </div>

          {explorePool.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-5 text-sm text-gray-600">
              No other products available right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {exploreItems.map((p) => (
                <div
                  key={p.id}
                  className="bg-white rounded-3xl shadow-md overflow-hidden flex flex-col transition hover:shadow-lg"
                >
                  <button
                    type="button"
                    onClick={() => nav(`/product/${p.id}`)}
                    className="text-left"
                  >
                    <div className="bg-[#fff6f6] h-[300px] sm:h-[350px] lg:h-[390px] flex items-center justify-center p-5 sm:p-7">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="max-h-[260px] sm:max-h-[310px] lg:max-h-[345px] w-full object-contain"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-base font-semibold text-[#5c2f2f] line-clamp-2">
                        {p.name}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {money(p.price)}
                      </div>
                    </div>
                  </button>

                  <div className="p-5 pt-0 mt-auto">
                    <motion.button
                      type="button"
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleRecommendedAdd(p)}
                      className="w-full text-sm font-semibold rounded-xl py-2.5 bg-gray-100 hover:bg-gray-200 transition"
                    >
                      Add to cart
                    </motion.button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductPage;
